import React from "react";
import * as Sentry from "@sentry/react";
import type {Integration as SentryIntegration} from "@sentry/core";
import {useLocation, useNavigationType, createRoutesFromChildren, matchRoutes} from "react-router-dom";

import {Account, RegisteredUser, CustomDomainType} from "../Types";
import {domainType, getFrontendEnvironment, getPublicCredentials} from "./environment";
import maybeUninitializedPosthog, {PostHog} from "posthog-js";

let posthog: PostHog | null = null;

declare global {
  interface Window {
    SentryClient: ReturnType<typeof Sentry.init>;
  }
}

function getPosthogConfig(): Parameters<PostHog["init"]> | null {
  const {posthog_host, posthog_app_key, posthog_trust_center_key} = getPublicCredentials();

  const defaultConfig = {
    api_host: posthog_host,
    capture_pageview: false,
  };

  switch (domainType) {
    case CustomDomainType.TrustCenter:
      return [posthog_trust_center_key, defaultConfig];
    case CustomDomainType.Collect:
      return null;
    default:
      return [posthog_app_key, defaultConfig];
  }
}

export function analyticsInit() {
  try {
    const {environment, release} = getFrontendEnvironment();
    const {sentry_dsn} = getPublicCredentials();

    window.addEventListener("error", e => {
      if (e.error?._isNominal) {
        e.preventDefault();
      }
    });

    if (sentry_dsn) {
      const integrations: SentryIntegration[] = [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
          shouldCreateSpanForRequest(url) {
            // Avoid creating spans for download requests, as they cause
            // spurious "Large HTTP payload" errors in Sentry.
            return !url.endsWith("/download");
          },
        }),
        Sentry.captureConsoleIntegration({levels: ["error"]}),
        Sentry.replayIntegration({
          blockAllMedia: false,
          maskAllInputs: false,
          maskAllText: false,
        }),
      ];
      window.SentryClient = Sentry.init({
        dsn: sentry_dsn,
        environment,
        release,
        integrations,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        ignoreErrors: ["Non-Error promise rejection captured"],
        beforeSend(event, hint) {
          // Don't report "nominal" exceptions
          const originalException = hint.originalException as {_isNominal?: boolean} | undefined;
          if (originalException?._isNominal) {
            return null;
          }
          // Prevent grouping errors across different environments
          event.fingerprint = ["{{ default }}", environment];
          return event;
        },
      });
    }

    const posthogConfig = getPosthogConfig();

    if (posthogConfig != null) {
      maybeUninitializedPosthog.init(...posthogConfig);
      posthog = maybeUninitializedPosthog;
    }

    // Add our console.error interceptor after Sentry so that we can prevent non-errors getting to sentry.
    const originalError = console["error"];
    console["error"] = (...args) => {
      if (args[0] === "React Router caught the following error during render" && args[1]?._isNominal) {
        // Suppress stupid react errors
        return;
      } else if (args[0]?._isNominal) {
        return;
      }
      originalError(...args);
    };
  } catch (ex) {
    // Don't interrupt whatever the caller was doing
    console.error(ex);
  }
}

export function analyticsIdentifyUser({user_id, primary_email, name}: RegisteredUser) {
  Sentry.setUser({id: user_id, email: primary_email, username: name});
  posthog?.identify(user_id, undefined, {email: primary_email, name});
}

export function analyticsIdentifyAccount(account: Account) {
  Sentry.setTags({
    "account.id": account.account_id,
    "account.name": account.display_name,
    "account.type": account.type_,
  });

  posthog?.group("account", account.account_id, {
    accountName: account.display_name,
    accountType: account.type_,
  });
}
